import {
  CoverageMatchVm,
  CoverageModuleVm,
  CoverageQuestionVm,
  CoverageStatus,
  CoverageTotals,
  CoverageVideoVm,
  CoverageFixVm,
  CoverageWordingVm,
  QuizFixKey,
  QuizPlacementKind,
  QuizResolution,
  QuizVideoMatch,
  QuizVideoMatchData
} from '../../models/quiz-video-coverage.model';

/** TypeSafe noul thresholds; tune after SME review of the first full run. */
export const STRONG_MATCH = 0.9;
export const PARTIAL_MATCH = 0.5;
/** Spelling noul threshold; untuned like the coverage thresholds. */
export const SPELLING_FLAG = 0.5;
/** Section-prep Score levels, indexed by level. */
export const PREP_LEVELS = ['Not discussed', 'Mentioned only', 'Topic taught', 'Answer stated'];
const PREP_TONES: CoverageStatus[] = ['uncovered', 'partial', 'reworded', 'covered'];
/** A question counts as taught before its quiz when levels 2-3 hold at least this much probability. */
export const TAUGHT_PROBABILITY = 0.5;

/** Summary groups, in display order; statuses are listed in the same order so the stacked bar reads left to right by group. */
export const COVERAGE_GROUPS: { key: string; label: string }[] = [
  { key: 'taught', label: 'Taught before quiz' },
  { key: 'placement', label: 'Wrong place' },
  { key: 'gaps', label: 'Content gaps' },
  { key: 'unjudged', label: 'Not judged' }
];

/** Placement checks count a lesson when TypeSafe is at least this likely to say it teaches the topic (set in the matching script). */
const TEACHES_PERCENT = 80;
const TAUGHT_PERCENT = TAUGHT_PROBABILITY * 100;

export interface CoverageLegendItem {
  key: string;
  label: string;
  icon: string;
  /** What the status means, in plain language; also the tooltip text. */
  description: string;
  /** How the matching script and dashboard arrive at it. */
  calculation: string;
}

export const COVERAGE_STATUSES: (CoverageLegendItem & { key: CoverageStatus; group: string })[] = [
  { key: 'covered', label: 'Answer stated', icon: 'fa-check-circle', group: 'taught',
    description: 'The videos or attached lesson materials before its quiz state the correct answer outright.',
    calculation: `TypeSafe rates the question against those lessons together. "Topic taught" plus "Answer stated" reach `
      + `${TAUGHT_PERCENT}%, and "Answer stated" is the larger of the two.` },
  { key: 'reworded', label: 'Topic taught', icon: 'fa-spell-check', group: 'taught',
    description: 'The lessons before its quiz teach the topic, but the student has to reword it, combine lessons, '
      + 'or apply it to new numbers.',
    calculation: `Same ${TAUGHT_PERCENT}% bar as Answer stated, but "Topic taught" outweighs "Answer stated".` },
  { key: 'misplaced', label: 'Wrong quiz', icon: 'fa-random', group: 'placement',
    description: 'A later lesson in the same module teaches it, after its quiz.',
    calculation: `Not taught before its quiz (under ${TAUGHT_PERCENT}%), and TypeSafe is at least ${TEACHES_PERCENT}% sure `
      + 'a later lesson in its module teaches the topic as its own subject.' },
  { key: 'move', label: 'Move candidate', icon: 'fa-exchange-alt', group: 'placement',
    description: 'A lesson in a later module teaches it.',
    calculation: `Not taught before its quiz, and TypeSafe is at least ${TEACHES_PERCENT}% sure a lesson in a later module `
      + 'teaches it. Only used when no earlier lesson or later lesson in its own module does.' },
  { key: 'earlier', label: 'Taught earlier', icon: 'fa-history', group: 'placement',
    description: 'An earlier lesson in the course teaches it, so the question works as review.',
    calculation: `Not taught in the lessons its quiz covers, but TypeSafe is at least ${TEACHES_PERCENT}% sure a lesson the `
      + 'student watched earlier teaches it. This wins over Wrong quiz and Move.' },
  { key: 'partial', label: 'Mentioned only', icon: 'fa-adjust', group: 'gaps',
    description: 'The lessons bring up the subject but never explain what the question tests.',
    calculation: `Under ${TAUGHT_PERCENT}% taught, but "Mentioned only", "Topic taught" and "Answer stated" together reach `
      + `${TAUGHT_PERCENT}%. No other lesson passes the ${TEACHES_PERCENT}% check. Explaining only part of a multi-part answer counts here.` },
  { key: 'uncovered', label: 'Not covered', icon: 'fa-times-circle', group: 'gaps',
    description: 'The checked videos and attached lesson materials before its quiz do not teach the topic, '
      + 'and no other lesson clearly teaches it.',
    calculation: `"Not discussed" holds more than half of TypeSafe's rating, and no lesson elsewhere in the course passes `
      + `the ${TEACHES_PERCENT}% check.` },
  { key: 'noQuiz', label: 'Not on a quiz', icon: 'fa-unlink', group: 'unjudged',
    description: 'The question is not attached to any quiz.',
    calculation: 'With no quiz there are no lessons to compare against, so TypeSafe does not rate it.' },
  { key: 'noTranscript', label: 'No transcripts', icon: 'fa-closed-captioning', group: 'unjudged',
    description: 'The lessons its quiz covers have no transcripts yet.',
    calculation: 'Every lesson its quiz covers is missing a transcript, so there is nothing for TypeSafe to read. '
      + 'Transcribe those videos and rerun.' }
];

export const SPELLING_LEGEND: CoverageLegendItem = {
  key: 'spelling', label: 'Spelling & grammar', icon: 'fa-keyboard',
  description: 'The question or one of its answers has a typo, a wrong word such as "breaks" for "brakes", a grammar '
    + 'error, or wording unclear enough to confuse a student. Can overlap any coverage status.',
  calculation: 'TypeSafe is at least 50% sure the text has a misspelled or wrong word, or at least 50% sure it has a '
    + 'grammar or clarity problem. Text with a character lost on import, such as "It?s", is always flagged. '
    + '"Draft correction" asks AI for the smallest fix, and TypeSafe checks that the meaning and correct answer are unchanged.'
};

/** Suggested fixes, in the order the legend lists them. */
export const FIX_LABELS: Record<QuizFixKey, string> = {
  reword: 'Reword the question',
  add_content: 'Add content to a lesson',
  check_screen: 'Check on-screen content',
  retire: 'Retire the question',
  move_question: 'Move the question',
  keep_review: 'Keep as review',
  add_to_quiz: 'Add it to a quiz',
  align_wording: "Use the lesson's wording",
  transcribe: 'Transcribe the lessons'
};

/** Below this, the top fix is a lean rather than a clear pick, so the runner-up is shown too. */
export const FIX_CLEAR_PICK = 0.5;
/** A Wrong quiz / Move question that fits its quiz's topic this well can also stay, with content added before the quiz. */
export const FIX_FITS_TOPIC = 0.5;

const FIX_LEAN_NOTE = ` When the top pick is under ${FIX_CLEAR_PICK * 100}%, the dashboard marks it "Leaning" and shows the runner-up.`;

/** Legend entries for every suggested fix. */
export const FIX_LEGEND: CoverageLegendItem[] = [
  { key: 'fix-move_question', label: FIX_LABELS.move_question, icon: 'fa-arrow-right',
    description: 'Move it to the quiz right after the lesson that teaches it. Suggested for Wrong quiz and Move candidate, '
      + 'and for Taught earlier when it does not fit its current quiz.',
    calculation: 'Moving needs no new content, so it is the default for Wrong quiz and Move candidate. TypeSafe also rates how '
      + `well the question fits the topic of its current quiz; at ${FIX_FITS_TOPIC * 100}% or more, adding the explanation before `
      + 'this quiz is shown as the alternative.' },
  { key: 'fix-keep_review', label: FIX_LABELS.keep_review, icon: 'fa-redo',
    description: 'Taught earlier and it fits this quiz\'s topic, so it works as review.',
    calculation: `For Taught earlier, TypeSafe rates whether the question belongs to the subject of the lessons its quiz covers. `
      + `At ${FIX_FITS_TOPIC * 100}% or more it suggests keeping it; below that, moving it to the quiz after the lesson that taught it.` },
  { key: 'fix-add_content', label: FIX_LABELS.add_content, icon: 'fa-plus',
    description: 'The subject belongs in these lessons, but the narration never explains it.',
    calculation: 'For Mentioned only and Not covered, TypeSafe picks this fix and the lesson it sees as the natural place to '
      + 'teach the topic.' + FIX_LEAN_NOTE },
  { key: 'fix-reword', label: FIX_LABELS.reword, icon: 'fa-pen',
    description: 'The lessons explain a closely related point the question could test instead.',
    calculation: 'Offered only for Mentioned only. "Draft rewrite" asks AI for two versions in that lesson\'s words, and '
      + 'TypeSafe checks the lesson teaches each new answer.' + FIX_LEAN_NOTE },
  { key: 'fix-check_screen', label: FIX_LABELS.check_screen, icon: 'fa-desktop',
    description: 'The narration points at a chart, regulation, or worked example on screen that likely holds the answer.',
    calculation: 'Transcripts only capture speech, so TypeSafe picks this when the answer is probably shown rather than said. '
      + 'Watch that lesson before adding content.' + FIX_LEAN_NOTE },
  { key: 'fix-retire', label: FIX_LABELS.retire, icon: 'fa-archive',
    description: 'The question tests something this course does not set out to teach.',
    calculation: 'TypeSafe picks this when the topic is outside these lessons and the course. For questions on no quiz, '
      + 'it is suggested when no lesson in the course clearly teaches the answer.' + FIX_LEAN_NOTE },
  { key: 'fix-add_to_quiz', label: FIX_LABELS.add_to_quiz, icon: 'fa-link',
    description: 'For questions on no quiz: add it to the quiz after the lesson that teaches it.',
    calculation: 'The lesson is the course video TypeSafe rated at least 90% likely to teach the answer.' },
  { key: 'fix-align_wording', label: FIX_LABELS.align_wording, icon: 'fa-quote-right',
    description: 'Optional, for Topic taught: rephrase the question in the lesson\'s words so students recognize it.',
    calculation: 'Always offered for Topic taught. "Draft in lesson wording" asks AI for two versions that keep the same fact '
      + 'and correct answer, and TypeSafe checks the lesson teaches each one.' },
  { key: 'fix-transcribe', label: FIX_LABELS.transcribe, icon: 'fa-closed-captioning',
    description: 'For No transcripts: transcribe the lessons its quiz covers, then rerun the matching.',
    calculation: 'A rule: nothing can be judged until those lessons have transcripts.' }
];

/** Statuses whose suggested fix comes from the resolution pass. */
const FIX_STATUSES = new Set<CoverageStatus>(['partial', 'uncovered', 'misplaced', 'move', 'earlier', 'noQuiz']);

const percent = (p: number) => `${Math.round(p * 100)}%`;

/** Fix for Topic taught (optional wording alignment), drafted from the lesson that best teaches it. */
export function alignFix(lessonName: string, timeLabel: string, excerpt: string): CoverageFixVm {
  return {
    key: 'align_wording', label: FIX_LABELS.align_wording, percentLabel: '', leaning: false, optional: true,
    detail: `Optional: rephrase it in the words ${lessonName} uses at ${timeLabel}, so students recognize it.`,
    runnerUp: '', lessonName, excerpt, draftKind: 'align', draftLabel: 'Draft in lesson wording'
  };
}

export const TRANSCRIBE_FIX: CoverageFixVm = {
  key: 'transcribe', label: FIX_LABELS.transcribe, percentLabel: '', leaning: false, optional: false,
  detail: 'Transcribe the videos for the lessons its quiz covers, then rerun the matching.',
  runnerUp: '', lessonName: '', excerpt: '', draftKind: null, draftLabel: ''
};

/** Suggested fix from the resolution pass, for content gaps, placement statuses, and questions on no quiz. */
export function fixView(resolution: QuizResolution, match: QuizVideoMatch | undefined, status: CoverageStatus): CoverageFixVm {
  const probs = resolution.fixProbabilities || {};
  const ranked = (Object.keys(probs) as QuizFixKey[]).sort((a, b) => probs[b] - probs[a]);
  const next = ranked.find(k => k !== resolution.fix);
  const fit = resolution.fitProbability;
  const lesson = resolution.lessonName;
  const at = lesson && match ? `${lesson} at ${formatTime(match.startSeconds)}` : lesson;
  const target = resolution.targetLesson ? `${resolution.targetLesson} (${resolution.targetModule})` : 'the lesson that teaches it';
  const quiz = resolution.targetQuiz ? `${resolution.targetQuiz} in ${resolution.targetModule}` : `a quiz after ${target}`;
  // Content-gap fixes carry TypeSafe's choice probabilities; Keep as review / Move after Taught earlier carry topic fit.
  const top = resolution.fix in probs ? probs[resolution.fix]
    : status === 'earlier' && fit != null ? (resolution.fix === 'keep_review' ? fit : 1 - fit) : null;
  const leaning = top != null && top < FIX_CLEAR_PICK;
  const details: Partial<Record<QuizFixKey, string>> = {
    reword: `Reword it to test what ${at || 'the lessons before its quiz'} explains.`,
    add_content: `Add the missing explanation to ${lesson || 'a lesson before its quiz'}.`,
    check_screen: `Check what ${lesson || 'the lessons before its quiz'} shows on screen. The answer may be on a slide or chart `
      + 'that transcripts cannot capture.',
    retire: status === 'noQuiz' ? 'No lesson in the course clearly teaches it. Retire it, or add it to a quiz once a lesson covers it.'
      : 'Retire it if this course does not set out to teach it.',
    move_question: status === 'earlier' ? `Move it to ${quiz}, right after ${resolution.targetLesson || 'the lesson'} taught it; `
      + 'it does not fit the topic of its current quiz.' : `Move it to ${quiz}, right after ${resolution.targetLesson || 'the lesson'} teaches it.`,
    keep_review: `Keep it here as review. Students learned it earlier in ${target}.`,
    add_to_quiz: `Add it to ${quiz}, after ${resolution.targetLesson || 'the lesson'} teaches it.`
  };
  const fitsHere = status !== 'earlier' && resolution.fix === 'move_question' && fit != null && fit >= FIX_FITS_TOPIC;
  return {
    key: resolution.fix,
    label: FIX_LABELS[resolution.fix],
    percentLabel: top != null ? percent(top) : '',
    leaning,
    optional: false,
    detail: details[resolution.fix] || '',
    runnerUp: fitsHere ? `Or keep it on this quiz and add the explanation before it: it fits this quiz's topic (${percent(fit)}).`
      : leaning && next ? `Next most likely: ${FIX_LABELS[next]} (${percent(probs[next] || 0)})`
      : leaning && status === 'earlier' ? `Next most likely: ${FIX_LABELS[resolution.fix === 'keep_review' ? 'move_question' : 'keep_review']} (${percent(1 - top)})`
      : '',
    // Only a reword is drafted here, and it needs the lesson's words to reword toward.
    lessonName: resolution.fix === 'reword' && match ? lesson : '',
    excerpt: resolution.fix === 'reword' && match ? match.excerpt : '',
    draftKind: resolution.fix === 'reword' && match ? 'reword' : null,
    draftLabel: resolution.fix === 'reword' && match ? 'Draft rewrite' : ''
  };
}

/** Which lessons "before its quiz" means, and what the transcripts can't show; the legend's footnote. */
export const COVERAGE_SCOPE_NOTE = '"Before its quiz" means the lessons since the previous quiz in its module; a Module Final '
  + 'covers the whole module. Attached regulations and materials count when their text teaches the answer. '
  + 'Older files require the material check to be rerun. Transcripts do not capture content shown only on screen.';

const STATUS_DESCRIPTIONS = COVERAGE_STATUSES.reduce((map, s) => ({ ...map, [s.key]: s.description }), {} as Record<CoverageStatus, string>);

const STATUS_LABELS = COVERAGE_STATUSES.reduce((map, s) => ({ ...map, [s.key]: s.label }), {} as Record<CoverageStatus, string>);

export interface CoverageView {
  questions: CoverageQuestionVm[];
  modules: CoverageModuleVm[];
  videos: CoverageVideoVm[];
}

export interface CoverageFilter {
  courseId: number | null;
  moduleId: number | null;
  status: CoverageStatus | null;
  /** Only questions flagged for spelling or grammar; optional so other filter users can ignore it. */
  spelling?: boolean;
  search: string;
}

export function formatTime(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
}

/**
 * `homeScore` is the best match among the lessons the question's quiz covers (or its module when the quiz is not placed there);
 * `moduleScore` is the best match anywhere in its module.
 */
export function coverageStatus(homeScore: number, bestScore: number, bestConcept = 0, moduleScore = homeScore): CoverageStatus {
  if (homeScore >= STRONG_MATCH) {
    return 'covered';
  }
  if (moduleScore >= STRONG_MATCH) {
    return 'misplaced';
  }
  if (moduleScore < PARTIAL_MATCH && bestScore >= STRONG_MATCH) {
    return 'move';
  }
  if (bestConcept >= STRONG_MATCH) {
    return 'reworded';
  }
  return bestScore >= PARTIAL_MATCH ? 'partial' : 'uncovered';
}

/**
 * Status from the section-prep Score (topic coverage across the lessons before the quiz). `placement` says where else
 * the topic is taught when it is not taught before the quiz: later in its module, later in the course, or earlier.
 */
export function prepStatus(probabilities: number[], placement: QuizPlacementKind | null): CoverageStatus {
  const [, mentioned, topic, stated] = probabilities;
  if (topic + stated >= TAUGHT_PROBABILITY) {
    return stated >= topic ? 'covered' : 'reworded';
  }
  if (placement) {
    return placement;
  }
  return mentioned + topic + stated >= TAUGHT_PROBABILITY ? 'partial' : 'uncovered';
}

function scoreTone(score: number): CoverageStatus {
  return score >= STRONG_MATCH ? 'covered' : score >= PARTIAL_MATCH ? 'partial' : 'uncovered';
}

export function buildCoverageView(data: QuizVideoMatchData): CoverageView {
  const moduleNames = new Map(data.modules.map(m => [m.moduleId, m.name]));
  const lessonNames = new Map(data.lessons.map(l => [l.lessonId, l.name]));
  const quizNames = new Map(data.quizzes.map(q => [q.quizId, q.name]));

  const questions = data.questions.map(q => {
    const matches = [...q.matches].sort((a, b) => b.covers - a.covers);
    const best = matches[0];
    const moduleVideos = q.moduleVideoIds ? new Set(q.moduleVideoIds) : null;
    const inModule = (m: QuizVideoMatch) => (moduleVideos ? moduleVideos.has(m.videoId) : m.moduleId === q.moduleId);
    const section = q.quizSection && q.quizSection.videoIds.length ? q.quizSection : null;
    const inSection = (m: QuizVideoMatch) => (section ? section.videoIds.includes(m.videoId) : inModule(m));
    const moduleBest = matches.find(inModule);
    const home = matches.find(inSection);
    const homeScore = home ? home.covers : 0;
    const bestScore = best ? best.covers : 0;
    const conceptBest = matches.reduce((top, m) => ((m.concept || 0) > (top ? top.concept || 0 : 0) ? m : top), null);
    const prep = q.sectionPrep;
    const material = q.materialCoverage;
    const materialEvidence = material && material.evidence;
    const materialLesson = materialEvidence ? data.lessons.find(l => l.lessonId === materialEvidence.lessonId) : null;
    const taughtByMaterial = !!materialEvidence && !!materialLesson && materialLesson.moduleId === q.moduleId
      && material.lessonIds.includes(materialEvidence.lessonId) && material.probabilities.length === 4
      && material.probabilities[2] + material.probabilities[3] >= TAUGHT_PROBABILITY;
    const prepMatch = prep && prep.videoId != null ? matches.find(m => m.videoId === prep.videoId) : undefined;
    const strong = (m: QuizVideoMatch) => m.covers >= STRONG_MATCH || (m.concept || 0) >= STRONG_MATCH;
    const movedWithin = matches.find(m => inModule(m) && !inSection(m) && strong(m));
    const movedOut = matches.find(m => !inModule(m) && strong(m));
    // Prompt v4 files carry the placement verdict (it needs course order); older files derive it from per-video matches.
    const placement = q.placement !== undefined ? q.placement : null;
    const placementKind: QuizPlacementKind | null = q.placement !== undefined
      ? (placement ? placement.kind : null)
      : (movedWithin ? 'misplaced' : movedOut ? 'move' : null);
    // Older result files have no section-prep judgment; fall back to per-video fact matching.
    const prepTaught = !!prep && prep.probabilities[2] + prep.probabilities[3] >= TAUGHT_PROBABILITY;
    const useMaterial = taughtByMaterial && !prepTaught && !q.notOnQuiz && q.quizIds.length > 0;
    const status: CoverageStatus = q.notOnQuiz ? 'noQuiz'
      : useMaterial ? prepStatus(material.probabilities, null) : q.noTranscripts ? 'noTranscript'
      : prep ? prepStatus(prep.probabilities, placementKind)
      : coverageStatus(homeScore, bestScore, conceptBest ? conceptBest.concept : 0, moduleBest ? moduleBest.covers : 0);
    const attachedEvidence = prep && prep.materialEvidence || (useMaterial ? materialEvidence : null);
    const placedMatch = placement ? matches.find(m => m.videoId === placement.videoId) : undefined;
    const isPlaced = status === 'misplaced' || status === 'move' || status === 'earlier';
    const target = prep
      ? (isPlaced ? (placement ? placedMatch : status === 'misplaced' ? movedWithin : movedOut) : prepMatch)
      : (status === 'covered' ? home : status === 'misplaced' ? moduleBest : status === 'reworded' ? conceptBest : best);
    const targetModule = placement && target === placedMatch ? placement.moduleName : target ? moduleNames.get(target.moduleId) : '';
    const conceptTime = (m: QuizVideoMatch) => formatTime(m.conceptStartSeconds != null ? m.conceptStartSeconds : m.startSeconds);
    // The prep lesson name is the one in the question's own module; match lesson ids point at the first lesson using the video.
    const lessonOf = (m: QuizVideoMatch) => (m === prepMatch && prep.lessonName) || (placement && m === placedMatch && placement.lessonName)
      || lessonNames.get(m.lessonId) || 'Unknown lesson';
    const targetLabel = target
      ? `${lessonOf(target)} at ${status === 'reworded' ? conceptTime(target) : formatTime(target.startSeconds)}`
      : (prep && prep.lessonName) || '';
    const moduleName = moduleNames.get(q.moduleId) || `Module ${q.moduleId}`;
    const hinted = status === 'uncovered' && conceptBest && conceptBest.concept >= PARTIAL_MATCH ? conceptBest : null;
    const hintPercent = hinted ? Math.round(hinted.concept * 100) : 0;
    const rewordedSource = prep ? (prepMatch && prepMatch.concept != null ? prepMatch : null) : conceptBest;
    const wordingSource = attachedEvidence ? null : status === 'reworded' ? rewordedSource : hinted;
    const wording: CoverageWordingVm | null = wordingSource ? {
      lessonName: lessonNames.get(wordingSource.lessonId) || 'Unknown lesson',
      moduleName: moduleNames.get(wordingSource.moduleId) || `Module ${wordingSource.moduleId}`,
      isHome: inModule(wordingSource),
      timeLabel: conceptTime(wordingSource),
      conceptPercent: Math.round(wordingSource.concept * 100),
      literalPercent: Math.round(wordingSource.covers * 100),
      termNote: wordingSource.termInTranscript === false ? `“${q.correctAnswer}” is never said in this video.`
        : wordingSource.termInTranscript ? `“${q.correctAnswer}” is said in this video, but not tied to this question.` : '',
      excerpt: wordingSource.conceptExcerpt || wordingSource.excerpt,
      others: matches
        .filter(m => m !== wordingSource && (m.concept || 0) >= PARTIAL_MATCH)
        .map(m => ({ key: `${m.videoId}-${m.lessonId}`, label: `${lessonNames.get(m.lessonId) || 'Unknown lesson'} · ${Math.round(m.concept * 100)}%` }))
    } : null;

    const where = section ? 'before its quiz' : 'in its module';
    const probabilities = useMaterial ? material.probabilities : prep ? prep.probabilities : null;
    const prepLevel = probabilities ? probabilities.indexOf(Math.max(...probabilities)) : -1;
    const prepLevels = probabilities ? probabilities.map((p, i) => ({
      key: PREP_LEVELS[i], label: PREP_LEVELS[i], percent: Math.round(p * 100), tone: PREP_TONES[i], active: i === prepLevel
    })) : [];
    const prepRecommendation: Record<'covered' | 'reworded' | 'partial' | 'uncovered', string> = {
      covered: `Answer stated ${where}: ${targetLabel}.`,
      reworded: `The lessons ${where} teach this topic without stating the answer in these words (best: ${targetLabel || 'several lessons'}). `
        + 'Fine as is; align the wording if students miss it.',
      partial: `The lessons ${where} only mention this subject${targetLabel ? ` (closest: ${targetLabel})` : ''}. `
        + 'Add the missing detail to a video or rewrite the question.',
      uncovered: `No lesson ${where} discusses this topic. Add content or retire the question.`
    };
    const legacyRecommendation: Record<CoverageStatus, string> = {
      covered: section ? `Taught before its quiz: ${targetLabel}.` : `Taught in its own module: ${targetLabel}.`,
      misplaced: `Taught in this module (${targetLabel}), but not in the lessons its quiz covers. `
        + 'Move it to the quiz that follows that lesson.',
      reworded: `Taught with different wording in ${target && target.moduleId !== q.moduleId ? `${moduleNames.get(target.moduleId)}: ` : ''}`
        + `${targetLabel}.${target && target.termInTranscript === false ? ` The video never says “${q.correctAnswer}”.` : ''}`
        + ' Align the question wording with the video, or name the term in the video.',
      move: `Best taught in ${targetModule}: ${targetLabel}. Consider moving it there.`,
      earlier: `Not taught ${where}, but taught earlier in the course in ${targetModule}: ${targetLabel}. `
        + 'Students see it before this quiz, so it works as review; move it only if it belongs with that lesson.',
      partial: `Only partly covered (best: ${targetLabel}). Review the question wording or the video.`,
      uncovered: hinted
        ? `No video clearly teaches this answer, but ${lessonNames.get(hinted.lessonId) || 'a lesson'} at ${conceptTime(hinted)} `
          + `may cover it in different words (${hintPercent}%). Check that video before adding content or retiring the question.`
        : 'No video in this course teaches this answer. Add content or retire the question.',
      noQuiz: 'This question is not on any quiz, so its coverage was not judged. Add it to a quiz or retire it.',
      noTranscript: 'None of the lessons its quiz covers has a transcript yet, so coverage was not judged. '
        + 'Transcribe those videos and rerun the matching.'
    };
    const materialLabel = attachedEvidence ? `${attachedEvidence.lessonName} · ${attachedEvidence.documentName} · ${attachedEvidence.locator}` : '';
    const attachedTaught = !!attachedEvidence && (status === 'covered' || status === 'reworded');
    let recommendation = attachedTaught ? `Covered by ${useMaterial ? 'attached lesson material' : 'the lesson video and attached material'}: `
        + `${materialLabel}. Students can learn the answer from the provided sources.`
      : status === 'earlier' || status === 'noQuiz' || status === 'noTranscript' ? legacyRecommendation[status]
      : !prep || status === 'misplaced' || (status === 'uncovered' && hinted) ? legacyRecommendation[status]
      : status === 'move' ? `Not taught ${where}. ${legacyRecommendation.move}`
      : prepRecommendation[status];
    if (status === 'uncovered' || status === 'partial' || status === 'noTranscript') {
      if (!material) {
        recommendation = 'Video coverage only; attached lesson regulations and materials have not been checked. ' + recommendation;
      } else if (material.unreadableCount > 0) {
        recommendation = 'Some attached lesson materials could not be read. Review them before confirming a content gap. ' + recommendation;
      }
    }

    const toVm = (m: QuizVideoMatch, lessonName = lessonNames.get(m.lessonId) || 'Unknown lesson'): CoverageMatchVm => ({
      key: `${m.videoId}-${m.lessonId}`,
      lessonId: m.lessonId,
      lessonName,
      moduleName: moduleNames.get(m.moduleId) || `Module ${m.moduleId}`,
      isHome: inModule(m),
      isSection: !!section && inSection(m),
      scoreLabel: `${Math.round(m.covers * 100)}%`,
      scorePercent: Math.round(m.covers * 100),
      scoreTone: scoreTone(m.covers),
      timeLabel: formatTime(m.startSeconds),
      excerpt: m.excerpt,
      isBest: !!prepMatch && m === prepMatch,
      wordingLabel: (m.concept || 0) >= STRONG_MATCH && m.covers < STRONG_MATCH ? `Different wording · ${Math.round(m.concept * 100)}%` : '',
      termMissing: m.termInTranscript === false
    });
    const matchVms = matches.map(m => toVm(m));
    // Section lessons keep course order and their names in this module; lessons without a transcript show as untested.
    const sectionMatches: CoverageMatchVm[] = section
      ? section.lessons.map((name, i) => {
        const m = matches.find(x => x.videoId === section.videoIds[i]);
        return m ? toVm(m, name) : {
          key: `missing-${i}`, lessonId: 0, lessonName: name, moduleName, isHome: true, isSection: true, scoreLabel: 'No transcript',
          scorePercent: 0, scoreTone: 'uncovered' as CoverageStatus, isBest: false, timeLabel: '', excerpt: '', wordingLabel: '', termMissing: false
        };
      })
      : matches.filter(inModule).map(m => toVm(m, m === prepMatch && prep.lessonName ? prep.lessonName : undefined));
    const outside = matches.filter(m => !inSection(m));
    const otherMatches = outside.filter(m => m.covers >= PARTIAL_MATCH || (m.concept || 0) >= PARTIAL_MATCH).map(m => toVm(m));
    const evidenceTime = (m: QuizVideoMatch) => (status === 'reworded' ? conceptTime(m) : formatTime(m.startSeconds));
    const evidence = attachedEvidence
      ? { lessonName: attachedEvidence.lessonName, timeLabel: `${attachedEvidence.documentName} · ${attachedEvidence.locator}`,
          excerpt: attachedEvidence.excerpt }
      : prepMatch
      ? { lessonName: lessonOf(prepMatch), timeLabel: evidenceTime(prepMatch),
          excerpt: (status === 'reworded' && prepMatch.conceptExcerpt) || prepMatch.excerpt }
      : null;
    const reviewLabels = {
      reviewed: 'Text reviewed',
      'partially-reviewed': 'Some text reviewed; some unavailable',
      'not-available': 'Text not available to the coverage check',
      'not-reviewed': 'Text available, but not reviewed for this question'
    };
    const materialReferences = material && material.references ? material.references.map(ref => ({
      key: ref.key,
      documentName: ref.documentName,
      lessonName: ref.lessonName,
      locator: ref.locator,
      reviewLabel: reviewLabels[ref.textStatus],
      reviewedText: ref.reviewedLocators.length ? `Read: ${ref.reviewedLocators.join(', ')}` : '',
      unavailableText: ref.unavailableLocators.length ? `Unavailable: ${ref.unavailableLocators.join(', ')}` : '',
      supportsAnswer: ref.supportsAnswer
    })) : [];
    const materialReferencesNote = q.notOnQuiz ? 'This question is not on a quiz, so lesson materials were not judged.'
      : !material || !material.references ? 'Reference review details were not recorded in this result file.'
      : !materialReferences.length ? 'No regulations or attachments are linked to the lessons this quiz covers.' : '';

    // v8: placements beyond the headline are notes, so a borrowed quiz cannot silently flag this question.
    const headline = q.headlinePlacement;
    const placementNotes: string[] = [];
    if (q.labelMismatch) {
      placementNotes.push('Stored module label does not match where this quiz sits; judged at its actual placement.');
    }
    for (const p of q.placements || []) {
      if (headline && p.moduleId === headline.moduleId && p.courseId === headline.courseId && p.quizLesson === headline.quizLesson) {
        continue;
      }
      const label = (STATUS_LABELS as Record<string, string>)[p.status] || p.status;
      placementNotes.push(`At ${moduleNames.get(p.moduleId) || 'another module'} (${p.quizLesson}): ${label}`);
    }
    const quizList = q.quizIds.map(id => quizNames.get(id)).filter(Boolean).join(', ');
    const spelling = q.spelling;
    const misspelledWord = !!spelling && (spelling.misspelled >= SPELLING_FLAG || spelling.garbled.length > 0);
    const grammar = !!spelling && (spelling.grammar || 0) >= SPELLING_FLAG;
    const misspelled = misspelledWord || grammar;
    const typo = !misspelledWord ? '' : spelling.garbled[0] || spelling.word;
    const spellingLabel = !misspelled ? ''
      : [misspelledWord ? (typo ? `Spelling: “${typo}”` : 'Possible misspelling') : '', grammar ? 'Grammar or wording' : '']
        .filter(Boolean).join(' · ');
    const fixMatch = q.resolution && q.resolution.videoId != null ? matches.find(m => m.videoId === q.resolution.videoId) : undefined;
    const alignSource = status === 'reworded' && !attachedEvidence ? (rewordedSource || prepMatch) : undefined;
    const fix: CoverageFixVm | null = status === 'noTranscript' ? TRANSCRIBE_FIX
      : alignSource ? alignFix(lessonOf(alignSource), conceptTime(alignSource), alignSource.conceptExcerpt || alignSource.excerpt)
      : q.resolution && q.resolution.fix && FIX_STATUSES.has(status) ? fixView(q.resolution, fixMatch, status) : null;
    const vm: CoverageQuestionVm = {
      id: q.questionId,
      courseId: q.courseId,
      moduleId: q.moduleId,
      status,
      statusLabel: STATUS_LABELS[status],
      statusHint: STATUS_DESCRIPTIONS[status],
      question: q.question,
      correctAnswer: q.correctAnswer,
      moduleName,
      quizNames: quizList,
      metaLabel: [moduleName, quizList].filter(Boolean).join(' · '),
      rowSub: [moduleName, section && section.quizLesson].filter(Boolean).join(' · '),
      lessonsTitle: section ? `Lessons before ${section.quizLesson}` : 'Lessons in this module',
      prepLevels,
      prepAria: `${section ? 'Lessons before the quiz' : 'Module lessons'}: ${prepLevels.map(l => `${l.label} ${l.percent}%`).join(', ')}`,
      evidence,
      materialReferences,
      materialReferencesNote,
      sectionMatches,
      otherMatches,
      hiddenOtherCount: outside.length - otherMatches.length,
      misspelled,
      spellingLabel,
      fix,
      draftInput: q.type && q.answers && q.answers.length ? { type: q.type, question: q.question, answers: q.answers } : null,
      homeScore,
      bestScore,
      bestModuleId: status === 'move' && placement ? placement.moduleId : best ? best.moduleId : null,
      bestLabel: attachedEvidence ? materialLabel : targetLabel,
      wordingHint: hinted ? `Possible different wording · ${hintPercent}%` : '',
      wording,
      recommendation,
      placementNotes,
      matches: matchVms,
      searchText: `${q.question} ${q.correctAnswer} ${moduleName} ${quizList} ${q.questionId}`.toLowerCase()
    };
    return vm;
  });

  const moduleOrder = new Map(data.modules.map(m => [m.moduleId, m.order || 0]));
  const modules: CoverageModuleVm[] = data.modules
    .map(m => {
      const own = questions.filter(q => q.moduleId === m.moduleId);
      const count = (s: CoverageStatus) => own.filter(q => q.status === s).length;
      return {
        id: m.moduleId,
        courseId: m.courseId,
        name: m.name,
        total: own.length,
        covered: count('covered'),
        misplaced: count('misplaced'),
        reworded: count('reworded'),
        partial: count('partial'),
        moveOut: count('move'),
        earlier: count('earlier'),
        uncovered: count('uncovered'),
        noQuiz: count('noQuiz'),
        noTranscript: count('noTranscript'),
        moveIn: questions.filter(q => q.status === 'move' && q.bestModuleId === m.moduleId).length,
        misspelled: own.filter(q => q.misspelled).length,
        coveredPercent: own.length > count('noQuiz') + count('noTranscript')
          ? Math.round(((count('covered') + count('reworded')) / (own.length - count('noQuiz') - count('noTranscript'))) * 100) : 0
      };
    })
    .filter(m => m.total || m.moveIn)
    .sort((a, b) => a.courseId - b.courseId || moduleOrder.get(a.id) - moduleOrder.get(b.id));

  // A lesson video "teaches" a question when it is that question's best match at STRONG_MATCH or above.
  const strongByLesson = new Map<number, number>();
  data.questions.forEach(q => {
    const best = [...q.matches].sort((a, b) => b.covers - a.covers)[0];
    if (best && best.covers >= STRONG_MATCH) {
      strongByLesson.set(best.lessonId, (strongByLesson.get(best.lessonId) || 0) + 1);
    }
  });
  const moduleCourse = new Map(data.modules.map(m => [m.moduleId, m.courseId]));
  const videos: CoverageVideoVm[] = data.lessons
    .filter(l => moduleCourse.has(l.moduleId))
    .map(l => ({
      key: `${l.lessonId}`,
      courseId: moduleCourse.get(l.moduleId),
      moduleId: l.moduleId,
      moduleName: moduleNames.get(l.moduleId) || `Module ${l.moduleId}`,
      lessonName: l.name,
      strongCount: strongByLesson.get(l.lessonId) || 0
    }))
    .sort((a, b) => a.strongCount - b.strongCount || a.lessonName.localeCompare(b.lessonName));

  return { questions, modules, videos };
}

export function filterQuestions(questions: CoverageQuestionVm[], filter: CoverageFilter): CoverageQuestionVm[] {
  const search = filter.search.trim().toLowerCase();
  return questions.filter(q =>
    (!filter.courseId || q.courseId === filter.courseId) &&
    (!filter.moduleId || q.moduleId === filter.moduleId) &&
    (!filter.status || q.status === filter.status) &&
    (!filter.spelling || q.misspelled) &&
    (!search || q.searchText.includes(search)));
}

export function countTotals(questions: CoverageQuestionVm[]): CoverageTotals {
  const count = (s: CoverageStatus) => questions.filter(q => q.status === s).length;
  return {
    total: questions.length, covered: count('covered'), misplaced: count('misplaced'), reworded: count('reworded'),
    partial: count('partial'), move: count('move'), earlier: count('earlier'), uncovered: count('uncovered'), noQuiz: count('noQuiz'),
    noTranscript: count('noTranscript'),
    misspelled: questions.filter(q => q.misspelled).length
  };
}
